import React, { useState } from 'react';
import Input from '../../../components/ui/Input';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { buyerQueriesService } from '../../../services/supabaseService';

const RequestOrderForm = ({ beanId, beanName, language }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    quantityKg: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const content = {
    en: {
      title: 'Request Order',
      subtitle: 'Fill out the form to request a quote',
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      company: 'Company Name',
      quantity: 'Quantity (kg)',
      message: 'Additional Requirements',
      submit: 'Submit Request',
      submitting: 'Submitting...',
      successTitle: 'Request Submitted!',
      successMessage: 'We will contact you within 24 hours.',
      sendAnother: 'Send Another Request'
    },
    zh: {
      title: '请求订单',
      subtitle: '填写表格请求报价',
      name: '姓名',
      email: '电子邮件',
      phone: '电话号码',
      company: '公司名称',
      quantity: '数量 (公斤)',
      message: '附加要求',
      submit: '提交请求',
      submitting: '提交中...',
      successTitle: '请求已提交！',
      successMessage: '我们将在24小时内与您联系。',
      sendAnother: '发送另一个请求'
    }
  };

  const t = content?.[language];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const chinesePhoneRegex = /^\+?86[-\s]?1[3-9]\d{9}$/;

    if (!formData?.name?.trim()) {
      newErrors.name = language === 'en' ? 'Name is required' : '姓名为必填项';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = language === 'en' ? 'Email is required' : '电子邮件为必填项';
    } else if (!emailRegex?.test(formData?.email)) {
      newErrors.email = language === 'en' ? 'Invalid email format' : '电子邮件格式无效';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = language === 'en' ? 'Phone number is required' : '电话号码为必填项';
    }

    if (!formData?.quantityKg || formData?.quantityKg <= 0) {
      newErrors.quantityKg = language === 'en' ? 'Valid quantity is required' : '请输入有效数量';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = language === 'en' ? 'Message is required' : '消息为必填项';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Detect if Chinese buyer based on phone number
      const chinesePhoneRegex = /^\+?86[-\s]?1[3-9]\d{9}$/;
      const isChineseBuyer = chinesePhoneRegex?.test(formData?.phone) || 
                             formData?.phone?.startsWith('+86') ||
                             formData?.phone?.startsWith('86');

      await buyerQueriesService?.create({
        name: formData?.name,
        email: formData?.email,
        phone: formData?.phone,
        company: formData?.company || null,
        inquiryType: 'buyer',
        message: `${formData?.message}\n\nRequested Quantity: ${formData?.quantityKg} kg\nBean: ${beanName}`,
        beanId: beanId,
        isChineseBuyer: isChineseBuyer
      });

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        quantityKg: '',
        message: ''
      });
    } catch (error) {
      setErrors({ submit: error?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setSubmitSuccess(false);
  };

  if (submitSuccess) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
          {t?.successTitle}
        </h3>
        <p className="text-muted-foreground mb-6">
          {t?.successMessage}
        </p>
        <Button onClick={handleSendAnother} variant="outline">
          {t?.sendAnother}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6 sticky top-20">
      <div className="mb-4">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
          {t?.title}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t?.subtitle}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t?.name}
          type="text"
          name="name"
          value={formData?.name}
          onChange={handleChange}
          error={errors?.name}
          required
        />

        <Input
          label={t?.email}
          type="email"
          name="email"
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          required
        />

        <Input
          label={t?.phone}
          type="tel"
          name="phone"
          value={formData?.phone}
          onChange={handleChange}
          error={errors?.phone}
          required
        />

        <Input
          label={t?.company}
          type="text"
          name="company"
          value={formData?.company}
          onChange={handleChange}
        />

        <Input
          label={t?.quantity}
          type="number"
          name="quantityKg"
          value={formData?.quantityKg}
          onChange={handleChange}
          error={errors?.quantityKg}
          min="1"
          required
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t?.message}
          </label>
          <textarea
            name="message"
            value={formData?.message}
            onChange={handleChange}
            rows="4"
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {errors?.message && (
            <p className="text-sm text-error mt-1">{errors?.message}</p>
          )}
        </div>

        {errors?.submit && (
          <div className="bg-error/10 border border-error rounded-md p-3 text-sm text-error">
            {errors?.submit}
          </div>
        )}

        <Button
          type="submit"
          fullWidth
          disabled={isSubmitting}
          iconName={isSubmitting ? 'Loader2' : 'Send'}
          iconPosition="left"
        >
          {isSubmitting ? t?.submitting : t?.submit}
        </Button>
      </form>
    </div>
  );
};

export default RequestOrderForm;